import type {CSSProperties} from '@pinecast/styles';
import styled, {focusRing, REDUCED_MOTION} from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import type {LocalizedReactNode, NilableLocalizedReactNode} from './types';

const WrapperLabel = styled(
  'label',
  {
    alignItems: 'center',
    display: 'flex',
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 20,
    paddingLeft: 0,

    ':not(:empty) ~ .Checkbox--WrapperLabel': {
      marginTop: -12,
    },
    ':not(:empty) ~ hr + .Checkbox--WrapperLabel': {
      marginTop: 0,
    },
  },
  {className: 'Checkbox--WrapperLabel'},
);
const InvisibleCheckbox = styled(
  'input',
  (_, designColors) => ({
    border: 0,
    display: 'block',
    flex: '0 0',
    height: 0,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    opacity: 0,
    padding: 0,
    width: 0,

    ':checked ~ .CheckBox-text::before': {
      backgroundColor: designColors.accentBlue,
      borderColor: designColors.accentBlue,
      boxShadow: `0 0 0 0 ${designColors.accentBlueLight}, 0 1px 0 rgba(0, 0, 0, 0.1), 0 2px 0 rgba(0, 0, 0, 0.1)`,
    },
    ':checked ~ .CheckBox-text::after': {
      opacity: 1,
      transform: 'scale(1)',
    },
    // The input is invisible, so the box it checks shows the ring, in both
    // states. Outside the box: checked, it is the ring's own color in light
    // mode.
    ':focus-visible ~ .CheckBox-text::before': focusRing(designColors),
  }),
  {type: 'checkbox'},
);

const Text = styled(
  'div',
  (
    {
      $alignInput,
      $disabled,
    }: {
      $alignInput: 'top' | 'center';
      $disabled: boolean;
    },
    designColors,
  ) => ({
    display: 'flex',
    fontFamily: 'Monument Grotesk Bold',
    fontSize: 14,
    fontWeight: 500,
    flex: '1 1',
    opacity: $disabled ? 0.5 : 1,
    paddingLeft: 24,
    position: 'relative',
    transition: 'opacity 0.2s',

    '::before': {
      backgroundColor: '#fff',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: designColors.borderGrayAccent,
      borderRadius: 4,
      bottom: 0,
      boxShadow: `0 0 0 0 ${designColors.accentBlue}, 0 1px 0 rgba(0, 0, 0, 0.1), 0 2px 0 rgba(0, 0, 0, 0.1)`,
      content: '""',
      height: 16,
      left: 0,
      margin: $alignInput === 'top' ? '0 auto auto' : 'auto',
      position: 'absolute',
      top: $alignInput === 'top' ? 1 : 0,
      transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
      width: 16,
      zIndex: 1,
    },
    '::after': {
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg width='11' height='10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.49 3.17L3.83 8.83 1 6' stroke='%23fff' stroke-width='1.5'/%3E%3C/svg%3E\")",
      // backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
      bottom: 2,
      content: '""',
      height: 16,
      left: 0,
      margin: $alignInput === 'top' ? '0 auto auto' : 'auto',
      opacity: 0,
      position: 'absolute',
      top: $alignInput === 'top' ? 1 : 0,
      transform: 'scale(0.75)',
      transition: 'transform 0.2s, opacity 0.2s',
      width: 16,
      zIndex: 2,

      // The tick fades in at its full size, rather than growing into place
      // (WCAG 2.3.3).
      [REDUCED_MOTION]: {transform: 'none', transition: 'opacity 0.2s'},
    },
  }),
  {className: 'CheckBox-text'},
);

// The description under the label, in the regular face that the `subText` of a
// `Label` has, with a small gap above it.
const SubText = styled('span', {
  display: 'block',
  fontFamily: DEFAULT_FONT,
  fontWeight: 400,
  marginTop: 4,
});

const Checkbox = ({
  alignInput = 'center',
  checked,
  disabled,
  nativeEvents,
  onChange,
  style,
  subText,
  tabIndex,
  text,
  textStyle,
}: {
  alignInput?: 'top' | 'center';
  checked: boolean;
  disabled?: boolean;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLInputElement>['onBlur'];
    onChange: React.HTMLAttributes<HTMLInputElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLInputElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLInputElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLInputElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLInputElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLInputElement>['onKeyPress'];
  }>;
  onChange: (checkedValue: boolean) => void;
  style?: CSSProperties;
  /**
   * A description under the label, in a lighter face. It describes the
   * checkbox (`aria-describedby`) and is not part of its name, so a screen
   * reader says the label first and the description after it. Put what the
   * box is for in `text`, and what it does or why in here.
   */
  subText?: NilableLocalizedReactNode;
  tabIndex?: number;
  text: LocalizedReactNode;
  textStyle?: CSSProperties;
}) => {
  const id = React.useId();
  const textID = `${id}-text`;
  const subTextID = `${id}-subtext`;
  return (
    <WrapperLabel style={style}>
      <InvisibleCheckbox
        // The label wraps the description too, so it would be in the name.
        // Naming the box by the label's own text keeps it out.
        aria-describedby={subText ? subTextID : undefined}
        aria-labelledby={subText ? textID : undefined}
        checked={checked || false}
        disabled={disabled || false}
        {...nativeEvents}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.checked);
          if (nativeEvents && nativeEvents.onChange) {
            nativeEvents.onChange(e);
          }
        }}
        tabIndex={tabIndex}
      />
      <Text
        $alignInput={alignInput}
        $disabled={disabled || false}
        style={textStyle}
      >
        {subText ? (
          <span style={{display: 'block'}}>
            <span id={textID} style={{display: 'block'}}>
              {text}
            </span>
            <SubText id={subTextID}>{subText}</SubText>
          </span>
        ) : (
          text
        )}
      </Text>
    </WrapperLabel>
  );
};

export default Checkbox;
